<template>
    <div ref="componentRef"
         :class="[outerClass, colSize, { 'form-field-disabled': isFormDisabled }]">
        <label v-if="label"
               class="block"
               :class="{ 'required-label': hasRequiredAttr, [labelClass]: true }"
               :style="labelStyle">{{
                __(label)
            }}</label>
        <div style="position: relative; width: 100%">
            <div v-show="!selectedOption || isEditing">
                <Autocomplete :optionLabel="(opt) => opt?.label ? opt.label.replace(/<[^>]*>?/gm, '') : ''"
                              ref="autocompleteRef"
                              class="w-full"
                              :key="editingKey"
                              :scrollHeight="scrollHeight"
                              :class="{ 'required-field': hasRequiredAttr }"
                              :showClear="showClear"
                              :validation="validation"
                              :virtualScrollerOptions="{ itemSize: itemSize }"
                              :suggestions="filteredOptions"
                              :disabled="isFormDisabled"
                              @change="handleChange"
                              @complete="searchItems"
                              v-model="searchValue"
                              :pt="selectPt">
                    <template #option="slotProps">
                        <span v-html="slotProps.option.label"></span>
                    </template>
                </Autocomplete>
            </div>

            <div v-if="selectedOption && !isEditing && !isFormDisabled"
                 @click="activateInput"
                 class="w-full min-h-[2.5rem] rounded-md border bg-white px-3 py-2 text-sm shadow-sm border-gray-300 text-gray-900 cursor-text">
                <span v-html="selectedOption.label"></span>
            </div>

            <div v-if="selectedOption && !isEditing && isFormDisabled"
                 class="w-full min-h-[2.5rem] rounded-md border px-3 py-2 text-sm shadow-sm border-gray-300 text-gray-500 bg-gray-100 cursor-not-allowed">
                <span v-html="selectedOption.label"></span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch, computed } from 'vue';
import Autocomplete from 'primevue/autocomplete';
import { __, translatePrimeVueMsgs } from '@pages/Helper/utils.js'

translatePrimeVueMsgs()

const props = defineProps({
    fieldName: {
        type: String,
        required: true,
    },
    validation: {
        type: [Array, String],
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    size: {
        type: String,
        default: null
    },
    isFocus: {
        type: Boolean,
        default: false
    },
    isFormDisabled: {
        type: Boolean,
        default: false
    },
    options: {
        type: Array,
        default: [],
        required: true
    },
    change: {
        type: Function,
        required: false
    },
    filter: {
        type: Function,
        required: false,
    },
    filtering: {
        type: Boolean,
        default: true
    },
    showClear: {
        type: Boolean,
        default: false
    },
    scrollHeight: {
        type: String,
        default: '200px'
    },
    setFirst: {
        type: Boolean,
        default: false
    },
    labelClass: {
        type: String,
        default: null
    },
    labelStyle: {
        type: [Object, String],
        default: null
    },
    optionClass: {
        type: String,
        default: null
    },
    optionStyle: {
        type: [Object, String],
        default: null
    },
    inputClass: {
        type: [Object, String],
        default: null
    },
    inputStyle: {
        type: [Object, String],
        default: null
    },

    inRepeater: {
        type: Boolean,
        default: false
    },
    itemSize: {
        type: Number,
        default: 38
    },
    default: {
        type: [String, Number],
        default: null
    }
});

const field = defineModel('field');
const validation = ref(Array.isArray(props.validation) ? props.validation : [props.validation]);
const colSize = props?.size ? 'col-' + props.size : 'col';
const hasRequiredAttr = validation.value.includes('required');
const outerClass = ref('field');
const filteredOptions = ref(props.options);
const searchValue = ref(null);
const isEditing = ref(false);
const componentRef = ref(null);
const editingKey = ref(0);

const selectPt = {
    root: ({ state }) => ({
        name: props.fieldName,
        class: [
            'w-full min-h-[2.5rem] flex items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-sm shadow-sm transition',
            'border-gray-300 text-gray-900',
            'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200',
            'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
            state?.overlayVisible ? 'ring-2 ring-blue-200 border-blue-500' : ''
        ]
    }),
    label: {
        style: props.inputStyle ?? props.optionStyle,
        class: [
            'flex-1 min-w-0 truncate',
            props.inputClass ?? props.optionClass
        ]
    },
    clearIcon: { class: 'text-gray-400 hover:text-gray-600 cursor-pointer' },
    dropdown: { class: 'ml-auto inline-flex items-center justify-center text-gray-500' },
    dropdownIcon: { class: 'text-gray-500' },
    overlay: { class: 'mt-1 rounded-md border border-gray-200 bg-white shadow-lg' },
    header: { class: 'px-3 py-2 border-b border-gray-100 bg-white' },
    pcFilterContainer: {
        root: { class: 'relative' }
    },
    pcFilter: {
        root: {
            class: 'w-full rounded-md border border-gray-200 bg-white pl-3 pr-10 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
        }
    },
    pcFilterIconContainer: {
        root: { class: 'absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none' }
    },
    filterIcon: { class: 'w-4 h-4' },
    listContainer: { class: 'overflow-auto' },
    list: { class: 'py-1' },
    option: ({ context }) => ({
        style: props.optionStyle,
        class: [
            'px-3 py-2 text-sm cursor-pointer select-none flex items-center gap-2',
            context?.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50',
            context?.focused ? 'bg-gray-50' : '',
            context?.selected ? 'bg-primary-50 text-blue-700' : 'text-gray-700',
            props.optionClass
        ]
    }),
    optionLabel: { class: 'flex-1' },
    optionCheckIcon: { class: 'w-4 h-4 text-blue-600' },
    optionBlankIcon: { class: 'w-4 h-4 text-transparent' },
    emptyMessage: { class: 'px-3 py-2 text-sm text-gray-500' },
    input: {
        class: [
            'flex-1 w-full min-w-0 bg-transparent border-0 outline-none',
            'shadow-none p-0 m-0 text-sm text-gray-900'
        ],
        style: props.inputStyle ?? props.optionStyle
    }
}

if (props.inRepeater) {
    outerClass.value = null;
}

const autocompleteRef = ref(null);

// ------------------------------------------------------------------------------

watch(() => ({ field: field.value, options: props.options, default: props.default }), async (newValue, oldValue) => {
    await nextTick();

    if (props.setFirst && (field.value === null || field.value === undefined) && newValue?.options?.length > 0) {
        field.value = newValue.options[0].value;
    }
    if (props.default && (field.value === null || field.value === undefined)) {
        field.value = props.default;
    }

    // Dopasowanie wartości field do jednej z wartości w options niezależnie od typu danych
    const matchedOption = newValue.options.find(option => String(option.value) === String(field.value));
    if (matchedOption) {
        field.value = matchedOption.value;
    }
}, { immediate: true });

// ------------------------------------------------------------------------------

watch(() => props.options, (newOptions) => {
    filteredOptions.value = newOptions;
});

// ------------------------------------------------------------------------------

watch(searchValue, (val) => {
    if (val && typeof val === 'object') {
        nextTick(() => {
            searchValue.value = null;
        });
    }
});

// ------------------------------------------------------------------------------

onMounted(async () => {
    if (props.isFocus) {
        await nextTick()
        autocompleteRef.value.$el.children[0]?.focus();
    }
});

// ------------------------------------------------------------------------------

const handleOutsideClick = (event) => {
    if (componentRef.value && !componentRef.value.contains(event.target)) {
        setTimeout(() => {
            if (!isEditing.value) return; // handleChange już to obsłużył
            isEditing.value = false;
            searchValue.value = null;
        }, 50);
    }
};

// ------------------------------------------------------------------------------

onMounted(async () => {
    document.addEventListener('mousedown', handleOutsideClick);
    if (props.isFocus) {
        await nextTick();
        autocompleteRef.value?.$el?.querySelector('input')?.focus();
    }
});

// ------------------------------------------------------------------------------

onUnmounted(() => {
    document.removeEventListener('mousedown', handleOutsideClick);
});

// ------------------------------------------------------------------------------

const activateInput = async () => {
    editingKey.value++;         // Nowa czysta instancja Autocomplete
    isEditing.value = true;
    searchValue.value = null;
    await nextTick();
    autocompleteRef.value?.$el?.querySelector('input')?.focus();
};

// ------------------------------------------------------------------------------

const selectedOption = computed(() => {
    if (field.value === null || field.value === undefined) return null;
    return props.options.find(opt => String(opt.value) === String(field.value)) ?? null;
});

// ------------------------------------------------------------------------------

const handleChange = (e) => {
    const val = e.value;
    const value = val && typeof val === 'object' ? val.value : val;
    field.value = value;
    if (props.change) {
        props.change({ ...e, value });
    }
    searchValue.value = null;
    isEditing.value = false;
};

// ------------------------------------------------------------------------------

const searchItems = (event) => {
    // Jeśli parent przekazał filter — deleguj do niego (serwer-side)
    if (props.filter) {
        props.filter(event);
        return;
    }
    // W przeciwnym razie — lokalne filtrowanie
    if (!event.query) {
        filteredOptions.value = props.options;
        return;
    }
    const query = event.query.toLowerCase();
    filteredOptions.value = props.options.filter(opt =>
        opt.label.replace(/<[^>]*>?/gm, '').toLowerCase().includes(query)
    );
};


// ------------------------------------------------------------------------------

</script>

<style lang="scss" scoped>
.form-field-disabled {
    :deep([data-p="disabled"]) {
        background-color: var(--p-select-disabled-background, #f5f6f8) !important;

        // Wszystkie dzieci również
        &>* {
            background-color: var(--p-select-disabled-background, #f5f6f8) !important;
        }
    }
}

:deep(input) {
    flex: 1;
    width: 100% !important;
    min-width: 0;
    background: transparent !important;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    margin: 0 !important;
    font-size: 0.875rem !important;
    color: inherit !important;
}
</style>
